<template>
  <div class="container">
    <div class="steps bg-white p-5">
      <!-- NOTE: Link Calendar Setup -->
      <div
        class="step"
        :class="{
          'step-open': setupState.calendar !== 'done',
          'step-success': setupState.calendar === 'done',
          'step-active': setupState.calendar === 'active',
          'step-error': setupState.calendar === 'skip',
        }"
        ref="calendar"
      >
        <div class="step-marker">
          <div class="step-marker-title">
            STEP
          </div>
          <div class="step-marker-number">
            1
          </div>
        </div>
        <div class="step-content">
          <div class="step-header">
            <button
              type="button"
              class="btn btn-borderless-primary header"
              @click="chooseLink('calendar')"
            >
              {{ $t('Setup.SetupCalendar') }}
            </button>
          </div>
          <div class="step-body">
            <link-calendar-setup />
          </div>
          <div class="step-footer">
            <button
              type="button"
              class="btn btn-primary text-uppercase mt-4"
              :disabled="setupState.calendar !== 'active'"
              @click="goNext('calendar')"
            >
              {{ $t('Setup.Button.GoNext') }}
            </button>
          </div>
        </div>
      </div>

      <!-- NOTE: Link Room Setup -->
      <div
        class="step"
        :class="{
          'step-open': setupState.roomResource !== 'done',
          'step-success': setupState.roomResource === 'done',
          'step-active': setupState.roomResource === 'active',
          'step-error': setupState.roomResource === 'skip',
          'step-close': setupState.schedulingCommon === 'disable'
        }"
        ref="roomResource"
      >
        <div class="step-marker">
          <div class="step-marker-title">
            STEP
          </div>
          <div class="step-marker-number">
            2
          </div>
        </div>
        <div class="step-content">
          <div class="step-header">
            <button
              type="button"
              class="btn btn-borderless-primary header"
              @click="chooseLink('roomResource')"
              disabled="role === 'Member'"
            >
              {{ $t('Setup.SetupRoomResource') }}
            </button>
          </div>
          <template v-if="role !== 'Member'">
            <div class="step-body">
              <meeting-room-setup
                @updateRooms="updateRooms"
              />
              <div
                v-if="editRoomData.length < 1"
                class="row justify-content-md-center allow-skip-text"
              >
                {{ $t('Setup.SkipFlow') }}
              </div>
            </div>
            <div class="step-footer">
              <template v-if="iseditRoomData">
                <button
                  type="button"
                  class="btn btn-primary text-uppercase mt-4"
                  :disabled="!iseditRoomData"
                  @click="saveRoomsHandler"
                >
                  {{ $t('Setup.Button.SaveAndGoNext') }}
                </button>
                <button
                  type="button"
                  class="btn btn-primary text-uppercase mt-4"
                  :disabled="setupState.roomResource !== 'active'"
                  @click="goNext('roomResource', true)"
                >
                  {{ $t('Setup.Button.Skip') }}
                </button>
              </template>
              <button
                v-else
                type="button"
                class="btn btn-primary text-uppercase mt-4"
                :disabled="setupState.roomResource !== 'active'"
                @click="goNext('roomResource')"
              >
                {{ $t('Setup.Button.GoNext') }}
              </button>
            </div>
          </template>
        </div>
      </div>

      <!-- NOTE: Link SmartScheduling Common Setting -->
      <div
        class="step"
        :class="{
          'step-open': setupState.schedulingCommon !== 'done',
          'step-success': setupState.schedulingCommon === 'done',
          'step-active': setupState.schedulingCommon === 'active',
          'step-error': setupState.schedulingCommon === 'skip',
          'step-close': setupState.schedulingCommon === 'disable'
        }"
        ref="schedulingCommon"
      >
        <div class="step-marker">
          <div class="step-marker-title">
            STEP
          </div>
          <div class="step-marker-number">
            3
          </div>
        </div>
        <div class="step-content">
          <div class="step-header">
            <button
              type="button"
              class="btn btn-borderless-primary header"
              @click="chooseLink('schedulingCommon')"
              disabled="role === 'Member'"
            >
              {{ $t('Setup.SetupSchedulingCommon') }}
            </button>
          </div>
          <template v-if="role !== 'Member'">
            <div class="step-body">
              <scheduling-common-setup
                @updateSchedulingCommon="updateSchedulingCommon"
              />
            </div>
            <div class="step-footer">
              <button
                type="button"
                class="btn btn-primary text-uppercase mt-4"
                :disabled="!isEditedSchedulingCommon || setupState.schedulingCommon !== 'active'"
                @click="saveCompanySettingHandler"
              >
                {{ $t('Setup.Button.SaveAndGoNext') }}
              </button>
              <button
                type="button"
                class="btn btn-primary text-uppercase mt-4"
                :disabled="setupState.schedulingCommon !== 'active'"
                @click="goNext('schedulingCommon', true)"
              >
                {{ $t('Setup.Button.Skip') }}
              </button>
            </div>
          </template>
        </div>
      </div>

      <!-- NOTE: Link SmartScheduling Personal Setting -->
      <div
        class="step"
        :class="{
          'step-open': setupState.personal !== 'done',
          'step-success': setupState.personal === 'done',
          'step-active': setupState.personal === 'active',
          'step-error': setupState.personal === 'skip'
        }"
        ref="personal"
      >
        <div class="step-marker">
          <div class="step-marker-title">
            STEP
          </div>
          <div class="step-marker-number">
            {{ role === 'Member' ? 2 : 4 }}
          </div>
        </div>
        <div class="step-content">
          <div class="step-header">
            <button
              type="button"
              class="btn btn-borderless-primary header"
              @click="chooseLink('personal')"
            >
              {{ $t('Setup.SetupPersonal') }}
            </button>
          </div>
          <div class="step-body">
            <scheduling-personal-setup
              @updateUserSetting="updateUserSetting"
            />
          </div>
          <div class="step-footer">
            <template v-if="role !== 'Member'">
              <button
                type="button"
                class="btn btn-primary text-uppercase mt-4"
                :disabled="!isEditUserSetting || setupState.personal !== 'active'"
                @click="saveUserSettingHandler"
              >
                {{ $t('Setup.Button.SaveAndGoNext') }}
              </button>
              <button
                type="button"
                class="btn btn-primary text-uppercase mt-4"
                :disabled="setupState.personal !== 'active'"
                @click="goNext('personal', true)"
              >
                {{ $t('Setup.Button.Skip') }}
              </button>
            </template>
            <button
              v-else
              type="button"
              class="btn btn-primary text-uppercase mt-4"
              :disabled="setupState.personal !== 'active'"
              @click="saveUserSettingAndOpenModalHandler"
            >
              {{ $t('Setup.Button.FinishSetup') }}
            </button>
          </div>
        </div>
      </div>

      <!-- NOTE: Link Chat Setting -->
      <div
        class="step"
        :class="{
          'step-open': setupState.chat !== 'done',
          'step-success': setupState.chat === 'done',
          'step-active': setupState.chat === 'active',
          'step-close': setupState.chat === 'disable'
        }"
        ref="chat"
      >
        <div class="step-marker">
          <div class="step-marker-title">
            STEP
          </div>
          <div class="step-marker-number">
            5
          </div>
        </div>
        <div class="step-content">
          <div class="step-header">
            <button
              type="button"
              class="btn btn-borderless-primary header"
              @click="chooseLink('chat')"
              disabled="role === 'Member'"
            >
              {{ $t('Setup.SetupChat') }}
            </button>
          </div>
          <template v-if="role !== 'Member'">
            <div class="step-body">
              <chat-setup />
            </div>
            <div class="step-footer">
              <button
                type="button"
                class="btn btn-primary text-uppercase mt-4"
                :disabled="setupState.chat !== 'active'"
                @click="isOpenModal=true"
              >
                {{ $t('Setup.Button.FinishSetup') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <welcome-modal :is-open.sync="isOpenModal" />
  </div>
</template>

<script>
import LinkCalendarSetup from './components/LinkCalendarSetup.vue'
import MeetingRoomSetup from './components/MeetingRoomSetup.vue'
import SchedulingCommonSetup from './components/SchedulingCommonSetup.vue'
import SchedulingPersonalSetup from './components/SchedulingPersonalSetup.vue'
import ChatSetup from './components/ChatSetup.vue'
import WelcomeModal from './components/WelcomeModal.vue'

import { mapState, mapActions, mapGetters } from 'vuex'

const nextTarget = {
  calendar: 'roomResource',
  roomResource: 'schedulingCommon',
  schedulingCommon: 'personal',
  personal: 'chat',
  chat: null
}

export default {
  title: 'Setup',
  components: {
    LinkCalendarSetup,
    MeetingRoomSetup,
    SchedulingCommonSetup,
    SchedulingPersonalSetup,
    ChatSetup,
    WelcomeModal,
  },
  data: () => ({
    state: {
      calendar: 'active',
      roomResource: 'yet',
      schedulingCommon: 'yet',
      personal: 'yet',
      chat: 'yet'
    },
    stepNum: 0,
    editRoomData: [],
    iseditRoomData: false,
    editSchedulingCommon: {},
    isEditedSchedulingCommon: false,
    editUserSetting: {},
    isEditUserSetting: false,
    isOpenModal: false
  }),
  computed: {
    ...mapState('auth', [
      'uid',
      'isNewUser',
      'role'
    ]),
    ...mapState('scheduling', [
      'setupState'
    ]),
    ...mapGetters('scheduling', [
      'getCurrentSetupState'
    ]),
  },
  mounted() {
    // NOTE: 既存設定情報の読み込み系
    this.setLoadingState('loadSetupData')
    this.setApiUserAuth().then(() => {
      return Promise.all([
        this.loadRooms(this.uid),
        this.loadCompanySetting(this.uid),
        this.loadUserSetting(this.uid),
      ])
    }).finally(() => {
      this.iseditRoomData = false
      this.isEditedSchedulingCommon = false
      this.isEditUserSetting = false
      this.unsetLoadingState('loadSetupData')
      return this.initSetupState()
    })
  },
  methods: {
    ...mapActions([
      'setLanguage',
      'setLoadingState',
      'unsetLoadingState',
      'setToastStack'
    ]),
    ...mapActions('auth', ['setApiUserAuth']),
    ...mapActions('scheduling', [
      'initSetupState',
      'setDoneState',
      'setSkipState',
      'setActiveState',
      'loadRooms',
      'saveRooms',
      'loadCompanySetting',
      'saveCompanySetting',
      'loadUserSetting',
      'saveUserSetting'
    ]),
    updateRooms (d) {
      this.editRoomData = [].concat(d)
      this.setActiveState('roomResource')
      this.iseditRoomData = true
    },
    saveRoomsHandler () {
      this.saveRooms({uid: this.uid, data: this.editRoomData}).then(() => {
        this.goNext('roomResource')
        this.setToastStack({
          type: 'success',
          title: this.$t('Setup.Toasts.SaveRooms.Title'),
          message: this.$t('Setup.Toasts.SaveRooms.Success'),
          show: 5
        })
      }).catch(() => {
        this.setToastStack({
          type: 'error',
          title: this.$t('Setup.Toasts.SaveRooms.Title'),
          message: this.$t('Setup.Toasts.SaveRooms.Error'),
          show: 5
        })
      })
    },
    updateSchedulingCommon (d) {
      this.editSchedulingCommon = Object.assign({}, d)
      this.setActiveState('schedulingCommon')
      this.isEditedSchedulingCommon = true
    },
    saveCompanySettingHandler () {
      this.saveCompanySetting({
        uid: this.uid,
        data: this.editSchedulingCommon
      }).then(() => {
        this.goNext('schedulingCommon')
        this.setToastStack({
          type: 'success',
          title: this.$t('Setup.Toasts.SaveCommon.Title'),
          message: this.$t('Setup.Toasts.SaveCommon.Success'),
          show: 5
        })
      }).catch(() => {
        this.setToastStack({
          type: 'error',
          title: this.$t('Setup.Toasts.SaveCommon.Title'),
          message: this.$t('Setup.Toasts.SaveCommon.Error'),
          show: 5
        })
      })
    },
    updateUserSetting (d) {
      this.editUserSetting = Object.assign({}, d)
      this.setActiveState('personal')
      if (d.userLang) {
        this.setLanguage(d.userLang)
      }
      this.isEditUserSetting = true
    },
    async saveUserSettingHandler () {
      this.saveUserSetting({
        uid: this.uid,
        data: this.editUserSetting
      }).then(() => {
        this.goNext('personal')
        this.setToastStack({
          type: 'success',
          title: this.$t('Setup.Toasts.SavePersonal.Title'),
          message: this.$t('Setup.Toasts.SavePersonal.Success'),
          show: 5
        })
      }).catch(() => {
        this.setToastStack({
          type: 'error',
          title: this.$t('Setup.Toasts.SavePersonal.Title'),
          message: this.$t('Setup.Toasts.SavePersonal.Error'),
          show: 5
        })
      })
    },
    saveUserSettingAndOpenModalHandler () {
      this.saveUserSettingHandler().then(() => {
        this.isOpenModal = true
      })
    },
    goNext (target, skip = false) {
      let next = nextTarget[target] ? nextTarget[target] : target
      if (this.role === 'Member' && target === 'calendar') {
        next = 'personal'
      }
      const data = {
        target,
        next
      }
      if (!skip) {
        this.setDoneState(data)
      } else {
        this.setSkipState(data)
      }
      this.$refs[data.next].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start',
      })
    },
    chooseLink (target) {
      this.setActiveState(target)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@abeja-inc/abeja-ui-library/scss/_variables.scss";

.allow-skip-text {
  font-size: $font-size-lg-1;
}

.step-footer {
  * + * {
    margin-left: 24px;
  }
}
</style>
